import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {
    safelyGetFilterByKey,
    safelyGetRelationshipFilterIdsFromFilterValue,
} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {ViewBoardIcon as BoardIcon} from 'shells/icon/svg/view-board-icon';
import {ViewListIcon as ListIcon} from 'shells/icon/svg/view-list-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';

import {writeLocalStorage} from '../../util/localStorage';

import {useGetSessionUser} from '../core/use-get-session-user';
import {PageContainer, PageTitleBar} from '../layout';
import {useGetCalendarSync, useGetCalendars} from '../setup/calendar/graphql/hooks';

import {CalendarEditModal} from './calendar/calendar-edit-modal';
import {CalendarOnboardingModal} from './calendar/calendar-onboarding-modal';
import {CalendarReconnectOnboardingModal} from './calendar/calendar-reconnect-onboarding-modal';
import {CalendarStatusButton} from './calendar/calendar-status-button';
import {MeetingPageButton} from './calendar/meeting-page-button';
import {CreateNewMeetingButton} from './create-new-meeting-button';
import {EmptyState} from './empty-state';
import {useGetMeetingsList} from './graphql/hooks/use-get-meetings-list';
import {SCHEDULER_VIEW_SEARCH, getFirstConflictCalendarError} from './helpers';
import {MeetingsBoardView} from './meetings-board-view';
import {MeetingsFilters} from './meetings-filters';
import {MeetingsListView} from './meetings-list-view';

type Props = ContextRouter & {
    activeFilters: FilterObject[];
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    onUpdateFilters: (arg1: FilterObject) => void;
};

export function MeetingsListPage(props: Props) {
    const [isOnboardingModalOpen, setIsOnboardingModalOpen] = React.useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);
    const [isReconnectOnboardingModalOpen, setIsReconnectOnboardingModalOpen] =
        React.useState<boolean>(false);

    // Support ?create=true from command palette AI action
    const shouldAutoCreate = props.urlParams.create === 'true';

    React.useEffect(() => {
        // Clear the ?create param from the URL so refresh doesn't re-trigger
        if (shouldAutoCreate) {
            const params = new URLSearchParams(props.location.search);
            params.delete('create');
            const cleanSearch = params.toString();
            props.history.replace({
                pathname: props.location.pathname,
                search: cleanSearch ? `?${cleanSearch}` : '',
            });
        }
    }, [shouldAutoCreate]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        writeLocalStorage(SCHEDULER_VIEW_SEARCH, props.location.search);
    }, [props.location.search]);

    const {user} = useGetSessionUser();

    const hostFilter = safelyGetFilterByKey(props.activeFilters, 'host');
    const hostIds = hostFilter
        ? safelyGetRelationshipFilterIdsFromFilterValue(hostFilter)
        : undefined;
    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';

    const typeFilter = safelyGetFilterByKey(props.activeFilters, 'meetingType');
    const typeIds = typeFilter
        ? safelyGetRelationshipFilterIdsFromFilterValue(typeFilter)
        : undefined;

    const {meetings, error, isLoading, isFirstLoad, refetchMeetings} = useGetMeetingsList({
        entityIds: hostIds,
        isShowingArchived,
        meetingTypes: typeIds,
    });

    const {
        calendarSync,
        refetch: refetchCalendarSync,
        isLoading: calendarIsLoading,
    } = useGetCalendarSync();

    const {calendars: conflictCalendars} = useGetCalendars();

    const selectedTab = props.urlParams.view || 'board';

    const filterOnlyCurrentUser = Boolean(
        hostIds && hostIds.length === 1 && user && hostIds[0] === user.id
    );
    // We want to show the empty state if there are no meetings and the user is filtering
    // to just their own meetings, which is the default filter
    const isFiltering =
        (props.activeFilters.length > 0 && !filterOnlyCurrentUser) || isShowingArchived;

    const tabOptions = [
        {
            label: 'Board',
            id: 'board',
            icon: BoardIcon,
            href: props.getUpdatedUrl({
                view: 'board',
            }),
            component: (
                <MeetingsBoardView
                    meetings={meetings}
                    activeFilters={props.activeFilters}
                    isLoading={isLoading}
                    error={error}
                    isFiltering={isFiltering}
                    emptyStateComponent={<EmptyState />}
                />
            ),
        },
        {
            label: 'Table',
            id: 'table',
            icon: ListIcon,
            href: props.getUpdatedUrl({
                view: 'table',
            }),
            component: (
                <MeetingsListView
                    meetings={meetings}
                    isLoading={isLoading}
                    isFirstLoad={isFirstLoad}
                    error={error}
                    isFiltering={isFiltering}
                />
            ),
        },
    ];

    const buttons = [
        <CalendarStatusButton
            key='calendar'
            calendarSyncStatus={calendarSync}
            conflictCalendars={conflictCalendars}
            refetchSyncStatus={refetchCalendarSync}
            error={calendarSync && calendarSync.syncErrors && calendarSync.syncErrors[0]}
            conflictSyncError={
                conflictCalendars && getFirstConflictCalendarError(conflictCalendars)
            }
            setIsOnboardingModalOpen={setIsOnboardingModalOpen}
            setIsEditModalOpen={setIsEditModalOpen}
            setIsReconnectOnboardingModalOpen={setIsReconnectOnboardingModalOpen}
        />,
        <CreateNewMeetingButton
            key='new-meeting'
            isAdmin={Boolean(user && user.permissions.canAccessSetup)}
            initialOpen={shouldAutoCreate}
        />,
    ];

    if (user) {
        buttons.unshift(
            <MeetingPageButton
                key='meeting-page-button'
                user={user}
                refetchMeetings={refetchMeetings}
            />
        );
    }

    return (
        <PageContainer
            title='Scheduler'
            pageTitleBar={
                <PageTitleBar
                    title='Scheduler'
                    subheaderPrimaryText='Simplify meeting planning'
                    buttons={buttons}
                />
            }
            controlBar={
                <ListPageControlBar
                    tabs={{
                        selectedTab: selectedTab,
                        options: tabOptions,
                    }}
                    archiveTab={{
                        isActive: isShowingArchived,
                        onChange: () => {
                            props.onUpdateFilters({
                                archived: isShowingArchived ? null : true,
                            });
                        },
                    }}
                    leftSection={
                        <MeetingsFilters
                            canViewAllMeetings={Boolean(
                                user && user.permissions.canViewAllMeetings
                            )}
                            activeFilters={props.activeFilters}
                            onUpdateFilters={props.onUpdateFilters}
                        />
                    }
                />
            }
        >
            <div>
                {/* @ts-expect-error */}
                <TabbedContent selectedTab={selectedTab} tabOptions={tabOptions} />
            </div>
            {isOnboardingModalOpen ? (
                <CalendarOnboardingModal
                    calendarSync={calendarSync}
                    isLoading={calendarIsLoading}
                    refetchSyncStatus={refetchCalendarSync}
                    onClose={() => setIsOnboardingModalOpen(false)}
                />
            ) : undefined}
            {isEditModalOpen ? (
                <CalendarEditModal
                    calendarSync={calendarSync}
                    isLoading={calendarIsLoading}
                    refetchSyncStatus={refetchCalendarSync}
                    setIsEditModalOpen={setIsEditModalOpen}
                />
            ) : undefined}
            {isReconnectOnboardingModalOpen ? (
                <CalendarReconnectOnboardingModal
                    calendarSync={calendarSync}
                    isLoading={calendarIsLoading}
                    refetchSyncStatus={refetchCalendarSync}
                    onClose={() => setIsReconnectOnboardingModalOpen(false)}
                />
            ) : undefined}
        </PageContainer>
    );
}
